// src/pages/Admin/Warehouse/ShipmentDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Package, Box, CheckCircle,
  Plus, RefreshCw, ChevronDown, ChevronUp,
  AlertCircle, Minus, X, Archive, Undo2,
  Trash2, MoveRight
} from 'lucide-react';
import { fulfillmentAPI } from '../../../api/fulfillment';
import { getTimeAgo } from '../../../utils/date';
import { formatPhoneNumber } from '../../../utils/phoneFormatter';
import OrderStatusBadge from '../Orders/components/OrderStatusBadge';
import showToast from '../../../utils/toast';
import ProductDetailModal from '../../../components/admin/ProductDetailModal';


interface ShipmentDetail {
  id: string;
  order_number: string;
  status: string;
  warehouse_name: string;
  created_at: string;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  items: {
    id: string;
    product_name: string;
    product_variant_name: string;
    quantity: number;
    price_at_sale: number;
    product_image: string;
    product_sku: string;
    vendor_name?: string;
    vendor_id?: number;
    images?: string[];  // All images for this variant/product
    additional_images?: string[];
  }[];
  packages: {
    id: string;
    package_number: number;
    status: string;
    items: {
      id: string;
      shipment_item: string;
      product_name: string;
      product_variant_name: string;
      quantity: number;
      product_image: string;
      product_sku: string;
      vendor_name?: string;
      vendor_id?: number;
      price: number;
      additional_images?: string[];
    }[];
  }[];
  shipping_address?: {
    line1: string;
    line2?: string;
    city: string;
    house_number: string;
    recipient_name: string;
    contact_number: string;
    contact_number_2?: string;
  };
}

interface UnassignedItem {
  shipment_item_id: string;
  product_name: string;
  product_variant_name: string;
  available_quantity: number;
  product_image: string;
  product_sku: string;
  vendor_name?: string;
  vendor_id?: number;
  additional_images?: string[];
}

const ShipmentDetail: React.FC = () => {
  const [removingItem, setRemovingItem] = useState<{
    packageId: string;
    shipmentItemId: string;
    currentQuantity: number;
    itemId: string;
    isLastItemType?: boolean;
    isLastPackage?: boolean;
  } | null>(null);
  const { shipmentId } = useParams<{ shipmentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const warehouseParam = searchParams.get('warehouse');
  const [shipment, setShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedPackage, setExpandedPackage] = useState<string | null>(null);
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [selectedItems, setSelectedItems] = useState<{ shipment_item_id: string; quantity: number; maxQuantity: number }[]>([]);
  const [expandedUnassigned, setExpandedUnassigned] = useState(true);
  const [removeQuantity, setRemoveQuantity] = useState<number>(1);
  const [maxRemoveQuantity, setMaxRemoveQuantity] = useState<number>(1);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // Assign items state
  const [assigningToPackage, setAssigningToPackage] = useState<string | null>(null);
  const [assignItems, setAssignItems] = useState<{ shipment_item_id: string; quantity: number; maxQuantity: number }[]>([]);

  // Delete package state
  const [deletingPackage, setDeletingPackage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [showLastItemConfirm, setShowLastItemConfirm] = useState(false);
  const [lastItemPackageId, setLastItemPackageId] = useState<string | null>(null);
  const [lastItemShipmentItemId, setLastItemShipmentItemId] = useState<string | null>(null);
  const [lastItemQuantity, setLastItemQuantity] = useState<number>(1);
  const [lastItemIsLastPackage, setLastItemIsLastPackage] = useState<boolean>(false);

  const [showUnpackConfirm, setShowUnpackConfirm] = useState(false);
  const [unpackingPackage, setUnpackingPackage] = useState<string | null>(null);
  const [willUnpackShipment, setWillUnpackShipment] = useState(false);
  const [unpackPackageNumber, setUnpackPackageNumber] = useState<number | null>(null);

  useEffect(() => {
    if (shipmentId) {
      fetchShipmentDetail();
    }
  }, [shipmentId]);

  // Expand first package by default when shipment loads
  useEffect(() => {
    if (shipment && shipment.packages.length > 0 && !expandedPackage) {
      setExpandedPackage(shipment.packages[0].id);
    }
  }, [shipment]);

  const fetchShipmentDetail = async () => {
    setLoading(true);
    try {
      const response = await fulfillmentAPI.getShipment(shipmentId!);
      if (!response.error && response.data) {
        setShipment(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch shipment:', error);
      showToast('Failed to load shipment details', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Calculate unassigned items
  const getUnassignedItems = (): UnassignedItem[] => {
    if (!shipment) return [];

    return shipment.items
      .map(item => {
        const assignedQuantity = shipment.packages.reduce((sum, pkg) =>
          sum + pkg.items.filter(i => i.shipment_item === item.id).reduce((s, i) => s + i.quantity, 0), 0
        );

        const available = item.quantity - assignedQuantity;

        return {
          shipment_item_id: item.id,
          product_name: item.product_name,
          product_variant_name: item.product_variant_name,
          available_quantity: available,
          product_image: item.product_image || '',
          product_sku: item.product_sku || '',
          vendor_name: item.vendor_name || '',
          vendor_id: item.vendor_id,
          additional_images: item.additional_images || []
        };
      })
      .filter(item => item.available_quantity > 0);
  };

  const unassignedItems = getUnassignedItems();
  const totalUnassigned = unassignedItems.reduce((sum, item) => sum + item.available_quantity, 0);

  // Computed values for empty packages - use useMemo for performance
  const hasEmptyPackage = React.useMemo(() => {
    return shipment?.packages.some(p => p.items.length === 0 && p.status === 'pending') || false;
  }, [shipment?.packages]);

  const emptyPackage = React.useMemo(() => {
    return shipment?.packages.find(p => p.items.length === 0 && p.status === 'pending') || null;
  }, [shipment?.packages]);

  // Remove item from package
  const handleRemoveItem = (
    packageId: string,
    shipmentItemId: string,
    currentQuantity: number,
    itemId: string,
    packageItemCount: number
  ) => {
    // Check if this is the only item type in the package
    const isLastItemTypeInPackage = packageItemCount === 1;
    const isLastPackage = shipment?.packages.length === 1;

    if (currentQuantity === 1) {
      // Single item, no quantity selector needed
      if (isLastItemTypeInPackage) {
        // Show delete confirmation modal
        setLastItemPackageId(packageId);
        setLastItemShipmentItemId(shipmentItemId);
        setLastItemQuantity(1); // Store the quantity to remove
        setLastItemIsLastPackage(isLastPackage);
        setShowLastItemConfirm(true);
      } else {
        // Remove directly
        handleRemoveFromPackage(packageId, shipmentItemId, 1, false);
      }
    } else {
      // Multiple quantities - show existing inline quantity selector
      setRemovingItem({
        packageId,
        shipmentItemId,
        currentQuantity,
        itemId,
        // Pass a flag to check if this is the only item type
        isLastItemType: isLastItemTypeInPackage,
        isLastPackage: isLastPackage
      });
      setRemoveQuantity(currentQuantity);
      setMaxRemoveQuantity(currentQuantity);
    }
  };

  const handleUnpackClick = (packageId: string, packageNumber: number, shipmentStatus: string) => {
    // Check if the shipment is fully packed
    const isShipmentPacked = shipmentStatus === 'packed';

    setUnpackingPackage(packageId);
    setUnpackPackageNumber(packageNumber);
    setWillUnpackShipment(isShipmentPacked);
    setShowUnpackConfirm(true);
  };

  const handleRemoveFromPackage = async (packageId: string, shipmentItemId: string, quantity: number, deleteIfEmpty: boolean = false) => {
    if (!shipment) return;

    try {
      const response = await fulfillmentAPI.removeItemFromPackage(packageId, {
        shipment_item_id: shipmentItemId,
        quantity: quantity,
        delete_if_empty: deleteIfEmpty  // Pass the flag to backend
      });

      if (!response.error) {
        if (response.data?.package_deleted) {
          showToast('Item removed and package deleted successfully', 'success');
        } else {
          showToast(`Removed ${quantity} item(s) from package`, 'success');
        }

        setRemovingItem(null);
        setRemoveQuantity(0);

        // Clean up last item state if it exists
        setShowLastItemConfirm(false);
        setLastItemPackageId(null);
        setLastItemShipmentItemId(null);
        setLastItemQuantity(0);

        // Refresh the data
        await fetchShipmentDetail();
      } else {
        showToast(response.error || 'Failed to remove item', 'error');
      }
    } catch (error) {
      console.error('Failed to remove item from package:', error);
      showToast('Failed to remove item from package', 'error');
    }
  };

  // Assign items to a package
  const handleAssignItems = async (packageId: string) => {

    if (!shipment || assignItems.length === 0) {
      console.log('No items to assign');
      showToast('Select at least one item', 'error');
      return;
    }

    try {
      const response = await fulfillmentAPI.assignItemsToPackage(packageId, {
        items: assignItems.map(item => ({
          shipment_item_id: item.shipment_item_id,
          quantity: item.quantity
        }))
      });
      console.log('Assign response:', response);

      if (!response.error) {
        showToast('Items assigned successfully', 'success');
        setAssigningToPackage(null);
        setAssignItems([]);
        fetchShipmentDetail();
      } else {
        showToast(response.error || 'Failed to assign items', 'error');
      }
    } catch (error) {
      console.error('Failed to assign items:', error);
      showToast('Failed to assign items', 'error');
    }
  };

  const updateAssignQuantity = (shipmentItemId: string, newQuantity: number) => {
    setAssignItems(prev =>
      prev.map(i =>
        i.shipment_item_id === shipmentItemId ? { ...i, quantity: Math.min(newQuantity, i.maxQuantity) } : i
      )
    );
  };

  // Create new package
  const handleCreatePackage = async () => {
    console.log('Creating package with items:', selectedItems);
    if (!shipment || selectedItems.length === 0) {
      showToast('Select at least one item', 'error');
      return;
    }

    try {
      const response = await fulfillmentAPI.createPackage(shipment.id, {
        items: selectedItems.map(item => ({
          shipment_item_id: item.shipment_item_id,
          quantity: item.quantity
        }))
      });
      console.log('Create package response:', response);

      if (!response.error) {
        showToast('Package created successfully', 'success');
        setShowCreatePackage(false);
        setSelectedItems([]);
        fetchShipmentDetail();
      }
    } catch (error) {
      console.error('Failed to create package:', error);
      showToast('Failed to create package', 'error');
    }
  };

  // Delete package
  const handleDeletePackage = async () => {
    if (!deletingPackage) return;

    try {
      const response = await fulfillmentAPI.removePackage(deletingPackage);
      if (!response.error) {
        showToast('Package deleted successfully', 'success');
        setDeletingPackage(null);
        setShowDeleteConfirm(false);
        // Also clear last item state if it was triggered
        setShowLastItemConfirm(false);
        setLastItemPackageId(null);
        setLastItemShipmentItemId(null);
        fetchShipmentDetail();
      } else {
        showToast(response.error || 'Failed to delete package', 'error');
        setDeletingPackage(null);
      }
    } catch (error) {
      console.error('Failed to delete package:', error);
      showToast('Failed to delete package', 'error');
      setDeletingPackage(null);
    }
  };

  const handleMarkPackagePacked = async (packageId: string) => {
    try {
      const response = await fulfillmentAPI.markPackagePacked(packageId);
      if (!response.error) {
        // Check if shipment status was also updated
        if (response.data?.shipment_status_updated) {
          showToast(
            response.data.message || 'Package packed! All packages are now packed. Shipment marked as ready for collection.',
            'success'
          );
        } else {
          showToast(response.data?.message || 'Package marked as packed', 'success');
        }
        fetchShipmentDetail();
      } else {
        showToast(response.error || 'Failed to mark package as packed', 'error');
      }
    } catch (error) {
      console.error('Failed to mark package as packed:', error);
      showToast('Failed to mark package as packed', 'error');
    }
  };

  const togglePackage = (packageId: string) => {
    setExpandedPackage(expandedPackage === packageId ? null : packageId);
  };

  const updateItemQuantity = (shipmentItemId: string, newQuantity: number) => {
    setSelectedItems(prev =>
      prev.map(i =>
        i.shipment_item_id === shipmentItemId ? { ...i, quantity: Math.min(newQuantity, i.maxQuantity) } : i
      )
    );
  };

  const goBack = () => {
    const params = new URLSearchParams();
    if (warehouseParam && warehouseParam !== 'all') {
      params.set('warehouse', warehouseParam);
    }
    navigate(`/dashboard/warehouse${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const totalItems = shipment?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const totalPackedItems = shipment?.packages.reduce((sum, pkg) =>
    sum + pkg.items.reduce((s, i) => s + i.quantity, 0), 0
  ) || 0;
  const totalPackages = shipment?.packages.length || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="text-center py-12 px-4">
        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Shipment not found</p>
        <button
          onClick={goBack}
          className="mt-4 text-blue-600 hover:text-blue-700"
        >
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  // Find the package being deleted
  const deletingPkg = shipment.packages.find(p => p.id === deletingPackage);

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
      {/* Back Button */}
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Dashboard</span>
      </button>

      {/* Order Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900">
                {shipment.order_number}
              </h1>
              <OrderStatusBadge status={shipment.status} size="sm" />
              {shipment.status === 'packed' && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Ready for Collection
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500">
              {shipment.warehouse_name} • {getTimeAgo(shipment.created_at)}
            </p>
          </div>
          <button
            onClick={fetchShipmentDetail}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors self-start"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Packing Progress */}
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Packing Progress</span>
            <span className="font-medium text-gray-900">
              {totalPackedItems} / {totalItems} items packed
            </span>
          </div>
          <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${totalItems > 0 ? (totalPackedItems / totalItems) * 100 : 0}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{totalPackages} package{totalPackages !== 1 ? 's' : ''}</span>
            <span>{totalUnassigned} unassigned</span>
          </div>
        </div>
      </div>

      {/* UNASSIGNED ITEMS SECTION */}
      <div className="bg-yellow-50 rounded-xl border border-yellow-200 overflow-hidden">
        <div
          className="px-4 py-3 flex items-center justify-between cursor-pointer"
          onClick={() => setExpandedUnassigned(!expandedUnassigned)}
        >
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-yellow-600" />
            <h3 className="text-sm font-semibold text-gray-700">
              Unassigned Items ({totalUnassigned})
            </h3>
            {totalUnassigned > 0 && (
              <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">
                Need packaging
              </span>
            )}
          </div>
          {expandedUnassigned ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>

        {expandedUnassigned && (
          <div className="px-4 pb-4 space-y-2">
            {unassignedItems.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">All items are assigned to packages</p>
            ) : (
              unassignedItems.map((item) => (
                <div
                  key={item.shipment_item_id}
                  className="flex items-center gap-3 p-2 bg-white rounded-lg border border-yellow-100 hover:bg-yellow-50 transition-colors"
                >
                  {/* Clickable Product Info - Opens Modal */}
                  <div
                    className="flex-1 min-w-0 flex items-center gap-3 cursor-pointer"
                    onClick={() => setSelectedProduct({
                      name: item.product_name,
                      variant_name: item.product_variant_name,
                      price: 0,
                      product_image: item.product_image,
                      vendor_name: item.vendor_name,
                      sku: item.product_sku,
                      images: item.additional_images || [item.product_image]  // All images for this variant
                    })}
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {item.product_image ? (
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/40x40?text=No+Image';
                          }}
                        />
                      ) : (
                        <Package className="w-full h-full p-2 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.product_name}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        {item.product_variant_name && <span>{item.product_variant_name}</span>}
                        {item.product_sku && <span className="font-mono">SKU: {item.product_sku}</span>}
                        {item.vendor_name && <span>Vendor: {item.vendor_name}</span>}
                      </div>
                    </div>
                  </div>

                  <span className="text-sm font-medium text-gray-700 flex-shrink-0">×{item.available_quantity}</span>

                  {/* Pack Button - Creates new package */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();// Prevent modal from opening

                      // Check if there's an empty package
                      if (hasEmptyPackage && emptyPackage) {
                        // Expand the empty package
                        setExpandedPackage(emptyPackage.id);
                        // Open the assign form
                        setAssigningToPackage(emptyPackage.id);
                        // Pre-select this item in the assign form
                        setAssignItems([{
                          shipment_item_id: item.shipment_item_id,
                          quantity: item.available_quantity,
                          maxQuantity: item.available_quantity
                        }]);
                        // Show toast
                        showToast(`Using existing empty Package #${emptyPackage.package_number}`, 'info');
                        return;
                      }

                      // No empty package, create a new one
                      setShowCreatePackage(true);
                      setSelectedItems([{
                        shipment_item_id: item.shipment_item_id,
                        quantity: item.available_quantity,
                        maxQuantity: item.available_quantity
                      }]);
                      setExpandedUnassigned(false);
                    }}
                    className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex-shrink-0"
                  >
                    Pack
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* CREATE PACKAGE BUTTON */}
      <button
        onClick={() => {
          if (hasEmptyPackage) {
            // If there's an empty package, navigate to it instead
            if (emptyPackage) {
              // Expand the empty package
              setExpandedPackage(emptyPackage.id);
              // Open the assign form for that package
              setAssigningToPackage(assigningToPackage === emptyPackage.id ? null : emptyPackage.id);
              // Show a toast to explain
              showToast(`Use existing empty Package #${emptyPackage.package_number} instead`, 'info');
            }
          } else {
            setShowCreatePackage(!showCreatePackage);
          }
        }}
        disabled={unassignedItems.length === 0}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg transition-colors text-sm font-medium ${unassignedItems.length === 0
          ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50'
          : hasEmptyPackage
            ? 'border-yellow-300 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 cursor-pointer'
            : 'border-blue-300 text-blue-600 hover:bg-blue-50 cursor-pointer'
          }`}
      >
        {hasEmptyPackage ? (
          <>
            <AlertCircle className="w-4 h-4" />
            Use Existing Empty Package #{emptyPackage?.package_number}
            <span className="text-xs opacity-75">(click to assign items)</span>
          </>
        ) : (
          <>
            <Plus className="w-4 h-4" />
            Create New Package
            {unassignedItems.length > 0 && (
              <span className="text-xs opacity-75">
                ({totalUnassigned} items available)
              </span>
            )}
          </>
        )}
      </button>

      {/* CREATE PACKAGE FORM */}
      {showCreatePackage && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Select items for new package
          </h4>

          {unassignedItems.length === 0 ? (
            <p className="text-sm text-gray-500">No unassigned items available</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {/* Unassigned Items in Create Package Form */}
              {unassignedItems.map((item) => {
                const isSelected = selectedItems.some(i => i.shipment_item_id === item.shipment_item_id);
                const selected = selectedItems.find(i => i.shipment_item_id === item.shipment_item_id);
                const selectedQty = selected?.quantity || 0;
                const maxQty = item.available_quantity;

                return (
                  <div
                    key={item.shipment_item_id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${isSelected ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    onClick={() => {
                      // Toggle selection - deselect if already selected
                      if (isSelected) {
                        setSelectedItems(prev => prev.filter(i => i.shipment_item_id !== item.shipment_item_id));
                      } else {
                        if (maxQty === 1) {
                          setSelectedItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: 1, maxQuantity: 1 }]);
                        } else {
                          setSelectedItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: maxQty, maxQuantity: maxQty }]);
                        }
                      }
                    }}
                  >
                    <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {item.product_image ? (
                        <img
                          src={item.product_image}
                          alt={item.product_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/48x48?text=No+Image';
                          }}
                        />
                      ) : (
                        <Package className="w-full h-full p-2 text-gray-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.product_name}</p>
                      {item.product_variant_name && (
                        <p className="text-xs text-gray-500">{item.product_variant_name}</p>
                      )}
                    </div>

                    {isSelected ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        {maxQty > 1 ? (
                          <>
                            <button
                              onClick={() => updateItemQuantity(item.shipment_item_id, Math.max(1, selectedQty - 1))}
                              className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <input
                              type="number"
                              min={1}
                              max={maxQty}
                              value={selectedQty}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                updateItemQuantity(item.shipment_item_id, Math.min(val, maxQty));
                              }}
                              className="w-12 text-center border border-gray-300 rounded py-1 text-sm"
                            />
                            <button
                              onClick={() => updateItemQuantity(item.shipment_item_id, Math.min(selectedQty + 1, maxQty))}
                              className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                            <span className="text-xs text-gray-500">/ {maxQty}</span>
                          </>
                        ) : (
                          <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                            ×1
                          </span>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (maxQty === 1) {
                            setSelectedItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: 1, maxQuantity: 1 }]);
                          } else {
                            setSelectedItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: maxQty, maxQuantity: maxQty }]);
                          }
                        }}
                        className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex-shrink-0"
                      >
                        Add
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {selectedItems.length > 0 && (
            <div className="mt-3 p-2 bg-blue-100 rounded-lg">
              <p className="text-sm font-medium text-gray-700">
                Selected: {selectedItems.length} item type(s) — {selectedItems.reduce((sum, i) => sum + i.quantity, 0)} total items
              </p>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleCreatePackage}
              disabled={selectedItems.length === 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Create Package ({selectedItems.reduce((sum, i) => sum + i.quantity, 0)} items)
            </button>
            <button
              onClick={() => {
                setShowCreatePackage(false);
                setSelectedItems([]);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* PACKAGES SECTION */}
      {shipment.packages.length > 0 ? (
        <div className="space-y-3">
          {shipment.packages.map((pkg) => {
            const isExpanded = expandedPackage === pkg.id;
            const packageItems = pkg.items || [];
            const itemCount = packageItems.reduce((sum, i) => sum + i.quantity, 0);

            return (
              <div key={pkg.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Package Header */}
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => togglePackage(pkg.id)}
                >
                  <div className="flex items-center gap-3">
                    <Box className="w-5 h-5 text-gray-500" />
                    <div>
                      <span className="font-medium text-gray-900">
                        Package #{pkg.package_number}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">
                        {itemCount} item{itemCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <OrderStatusBadge status={pkg.status} size="sm" />
                  </div>
                  <div className="flex items-center gap-2">
                    {pkg.status === 'pending' && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssigningToPackage(assigningToPackage === pkg.id ? null : pkg.id);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700"
                        >
                          <MoveRight className="w-3 h-3 inline mr-1" />
                          Assign
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkPackagePacked(pkg.id);
                          }}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700"
                        >
                          Pack
                        </button>
                      </>
                    )}
                    {pkg.status === 'packed' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnpackClick(pkg.id, pkg.package_number, shipment.status);
                        }}
                        className="px-3 py-1 bg-yellow-600 text-white text-xs rounded-lg hover:bg-yellow-700 flex items-center gap-1"
                      >
                        <Undo2 className="w-3 h-3" />
                        Unpack
                      </button>
                    )}
                    {shipment.packages.length > 1 && pkg.status === 'pending' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeletingPackage(pkg.id);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-1 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Assign Items Form */}
                {assigningToPackage === pkg.id && (
                  <div className="px-4 pb-2 pt-2 border-b border-gray-100 bg-blue-50">
                    <h5 className="text-xs font-medium text-gray-700 mb-2">Assign items to Package #{pkg.package_number}</h5>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {unassignedItems.length === 0 ? (
                        <p className="text-sm text-gray-500">No unassigned items available</p>
                      ) : (
                        /* Unassigned Items in Assign Form */
                        unassignedItems.map((item) => {
                          const isSelected = assignItems.some(i => i.shipment_item_id === item.shipment_item_id);
                          const selected = assignItems.find(i => i.shipment_item_id === item.shipment_item_id);
                          const selectedQty = selected?.quantity || 0;
                          const maxQty = item.available_quantity;

                          return (
                            <div
                              key={item.shipment_item_id}
                              className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${isSelected ? 'bg-blue-100 border-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                              onClick={() => {
                                // Toggle selection - deselect if already selected
                                if (isSelected) {
                                  setAssignItems(prev => prev.filter(i => i.shipment_item_id !== item.shipment_item_id));
                                } else {
                                  // Select with full quantity
                                  if (maxQty === 1) {
                                    setAssignItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: 1, maxQuantity: 1 }]);
                                  } else {
                                    setAssignItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: maxQty, maxQuantity: maxQty }]);
                                  }
                                }
                              }}
                            >
                              {/* Product Info - Clicking here also toggles selection, not opens modal */}
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                  {item.product_image ? (
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.src = 'https://via.placeholder.com/32x32?text=No+Image';
                                      }}
                                    />
                                  ) : (
                                    <Package className="w-full h-full p-1.5 text-gray-400" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-gray-900 truncate">{item.product_name}</p>
                                  {item.product_variant_name && (
                                    <p className="text-xs text-gray-500 truncate">{item.product_variant_name}</p>
                                  )}
                                </div>
                              </div>

                              {/* Quantity Controls */}
                              {isSelected ? (
                                <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  {maxQty > 1 ? (
                                    <>
                                      <button
                                        onClick={() => updateAssignQuantity(item.shipment_item_id, Math.max(1, selectedQty - 1))}
                                        className="w-5 h-5 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                      <input
                                        type="number"
                                        min={1}
                                        max={maxQty}
                                        value={selectedQty}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value) || 1;
                                          updateAssignQuantity(item.shipment_item_id, Math.min(val, maxQty));
                                        }}
                                        className="w-10 text-center border border-gray-300 rounded py-0.5 text-xs"
                                      />
                                      <button
                                        onClick={() => updateAssignQuantity(item.shipment_item_id, Math.min(selectedQty + 1, maxQty))}
                                        className="w-5 h-5 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                      <span className="text-xs text-gray-500">/ {maxQty}</span>
                                    </>
                                  ) : (
                                    <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">
                                      ×1
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (maxQty === 1) {
                                      setAssignItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: 1, maxQuantity: 1 }]);
                                    } else {
                                      setAssignItems(prev => [...prev, { shipment_item_id: item.shipment_item_id, quantity: maxQty, maxQuantity: maxQty }]);
                                    }
                                  }}
                                  className={`px-2 py-1 text-xs rounded flex-shrink-0 ${maxQty > 1
                                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                  {maxQty > 1 ? 'Add' : 'Assign'}
                                </button>
                              )}
                            </div>
                          );
                        })

                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleAssignItems(pkg.id)}
                        disabled={assignItems.length === 0}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Assign {assignItems.reduce((sum, i) => sum + i.quantity, 0)} items
                      </button>
                      <button
                        onClick={() => {
                          setAssigningToPackage(null);
                          setAssignItems([]);
                        }}
                        className="px-3 py-1 border border-gray-300 text-xs rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Package Items (Expanded) */}
                {isExpanded && (
                  <div className="px-4 pb-4 space-y-2 border-t border-gray-100 pt-3">
                    {packageItems.length === 0 && pkg.status === 'pending' ? (
                      <div className="px-4 pb-1">
                        <div className="bg-yellow-50 border-2 border-dashed border-yellow-300 rounded-lg p-4 text-center">
                          <Package className="w-8 h-8 text-yellow-400 mx-auto mb-1" />
                          <p className="text-sm text-yellow-700 font-medium">This package is empty</p>
                          <p className="text-xs text-yellow-600 mt-1">
                            Click the <span className="font-semibold">"Assign"</span> button above to add items
                          </p>
                        </div>
                      </div>
                    ) : (
                      packageItems.map((item) => {
                        const isRemoving = removingItem &&
                          removingItem.packageId === pkg.id &&
                          removingItem.shipmentItemId === item.shipment_item;

                        return (
                          <div key={item.id}>
                            <div className={`flex items-center gap-3 p-2 rounded-lg ${isRemoving ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
                              }`}>
                              {/* Clickable Product Info */}
                              <div
                                className="flex-1 min-w-0 flex items-center gap-3 cursor-pointer"
                                onClick={() => setSelectedProduct({
                                  name: item.product_name,
                                  variant_name: item.product_variant_name,
                                  price: item.price || 0,
                                  image: item.product_image,
                                  vendor_name: item.vendor_name,
                                  sku: item.product_sku,
                                  images: item.additional_images || [item.product_image]
                                })}
                              >
                                <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                                  {item.product_image ? (
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.src = 'https://via.placeholder.com/40x40?text=No+Image';
                                      }}
                                    />
                                  ) : (
                                    <Package className="w-full h-full p-2 text-gray-400" />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 truncate">{item.product_name}</p>
                                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                    {item.product_variant_name && <span>{item.product_variant_name}</span>}
                                    {item.product_sku && <span className="font-mono">SKU: {item.product_sku}</span>}
                                    {item.vendor_name && <span>Vendor: {item.vendor_name}</span>}
                                  </div>
                                </div>
                              </div>

                              <span className="text-sm text-gray-500 flex-shrink-0">×{item.quantity}</span>

                              {/* Remove button */}
                              {pkg.status === 'pending' && (
                                <button
                                  onClick={() => handleRemoveItem(pkg.id, item.shipment_item, item.quantity, item.id, pkg.items.length)}
                                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                  title="Remove item from package"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>

                            {/* Remove quantity dialog - displayed right below the item */}
                            {isRemoving && (
                              <div className="mt-2 ml-14 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium text-gray-700">
                                      Remove how many?
                                    </span>
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={() => setRemoveQuantity(Math.max(1, removeQuantity - 1))}
                                        className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                                      >
                                        <Minus className="w-3 h-3" />
                                      </button>
                                      <input
                                        type="number"
                                        min={1}
                                        max={maxRemoveQuantity}
                                        value={removeQuantity}
                                        onChange={(e) => {
                                          const val = parseInt(e.target.value) || 1;
                                          setRemoveQuantity(Math.min(val, maxRemoveQuantity));
                                        }}
                                        className="w-12 text-center border border-gray-300 rounded py-1 text-sm"
                                      />
                                      <button
                                        onClick={() => setRemoveQuantity(Math.min(removeQuantity + 1, maxRemoveQuantity))}
                                        className="w-6 h-6 flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300"
                                      >
                                        <Plus className="w-3 h-3" />
                                      </button>
                                      <span className="text-xs text-gray-500">/ {maxRemoveQuantity}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        if (removingItem) {
                                          const isRemovingAll = removeQuantity === removingItem.currentQuantity;
                                          const wouldEmptyPackage = isRemovingAll && removingItem.isLastItemType;

                                          if (wouldEmptyPackage) {
                                            // Show the "last item" confirmation modal
                                            setLastItemPackageId(removingItem.packageId);
                                            setLastItemShipmentItemId(removingItem.shipmentItemId);
                                            setLastItemQuantity(removeQuantity);
                                            setLastItemIsLastPackage(removingItem.isLastPackage || false);
                                            setShowLastItemConfirm(true);
                                            // Close the inline selector
                                            setRemovingItem(null);
                                            setRemoveQuantity(0);
                                          } else {
                                            // Remove the specified quantity
                                            handleRemoveFromPackage(
                                              removingItem.packageId,
                                              removingItem.shipmentItemId,
                                              removeQuantity,
                                              false
                                            );
                                          }
                                        }
                                      }}
                                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      onClick={() => setRemovingItem(null)}
                                      className="px-3 py-1 border border-gray-300 text-sm rounded hover:bg-gray-50"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500">No packages created yet</p>
          <p className="text-sm text-gray-400">Click "Create Package" to start packing</p>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && deletingPkg && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Delete Package</h3>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete <strong>Package #{deletingPkg.package_number}</strong>?
                {deletingPkg.items.length > 0 && (
                  <span className="block mt-2 text-yellow-700">
                    ⚠️ This package contains {deletingPkg.items.reduce((sum, i) => sum + i.quantity, 0)} item(s).
                    They will be moved to the unassigned list.
                  </span>
                )}
                {deletingPkg.items.length === 0 && (
                  <span className="block mt-2 text-gray-500">This package is empty and will be removed.</span>
                )}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeletingPackage(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePackage}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  Delete Package
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ORDER DETAILS SECTION (Bottom) */}
      <details className="bg-white rounded-xl border border-gray-200">
        <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 flex items-center gap-2">
          <span>Order Details</span>
          <ChevronDown className="w-4 h-4 ml-auto" />
        </summary>
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          {/* Customer Info */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Customer</h4>
            <div className="space-y-1 text-sm">
              <p className="font-medium text-gray-900">{shipment.customer_name}</p>
              {shipment.customer_email && (
                <p className="text-gray-600">{shipment.customer_email}</p>
              )}
              {shipment.customer_phone && (
                <p className="text-gray-600">{formatPhoneNumber(shipment.customer_phone)}</p>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          {shipment.shipping_address && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Shipping Address</h4>
              <div className="text-sm text-gray-600">
                <p>{shipment.shipping_address.recipient_name}</p>
                <p>{shipment.shipping_address.house_number} {shipment.shipping_address.line1}</p>
                {shipment.shipping_address.line2 && <p>{shipment.shipping_address.line2}</p>}
                <p>{shipment.shipping_address.city}</p>
                <p className="mt-1">{formatPhoneNumber(shipment.shipping_address.contact_number)}</p>
                {shipment.shipping_address.contact_number_2 && (
                  <p className="text-sm text-gray-500">{formatPhoneNumber(shipment.shipping_address.contact_number_2)} (secondary)</p>
                )}
              </div>
            </div>
          )}
        </div>
      </details>
      <ProductDetailModal
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct || { name: '', price: 0, image: '' }}
      />
      {/* LAST ITEM CONFIRMATION MODAL */}
      {showLastItemConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowLastItemConfirm(false)} />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center mb-4">
                <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">
                  {lastItemIsLastPackage ? 'Remove Last Item?' : 'Remove Last Item?'}
                </h3>
                {lastItemIsLastPackage ? (
                  <>
                    <p className="text-sm text-gray-500 mt-2">
                      This is the last item in the only package in this shipment.
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      The package cannot be deleted because shipments must have at least one package.
                    </p>
                    <p className="text-sm text-yellow-600 mt-2 font-medium">
                      The item will be moved to the unassigned list, but the empty package will remain.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mt-2">
                      This is the last item in this package. The package will be automatically deleted.
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      This action cannot be undone.
                    </p>
                    {lastItemQuantity && lastItemQuantity > 1 && (
                      <p className="text-sm text-gray-600 mt-2">
                        Removing all {lastItemQuantity} items from this package.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowLastItemConfirm(false);
                    setLastItemPackageId(null);
                    setLastItemShipmentItemId(null);
                    setLastItemQuantity(0);
                    setLastItemIsLastPackage(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (lastItemPackageId && lastItemShipmentItemId) {
                      // Remove the item but don't try to delete the package
                      handleRemoveFromPackage(
                        lastItemPackageId,
                        lastItemShipmentItemId,
                        lastItemQuantity || 1,
                        !lastItemIsLastPackage  // Only delete if not the last package
                      );

                      // Close modal
                      setShowLastItemConfirm(false);
                      setLastItemPackageId(null);
                      setLastItemShipmentItemId(null);
                      setLastItemQuantity(0);
                      setLastItemIsLastPackage(false);
                    }
                  }}
                  className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium ${lastItemIsLastPackage
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-red-600 hover:bg-red-700'
                    }`}
                >
                  {lastItemIsLastPackage ? 'Remove Item (Keep Package)' : 'Remove & Delete Package'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* UNPACK CONFIRMATION MODAL */}
      {showUnpackConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowUnpackConfirm(false)} />
            <div className="relative bg-white rounded-lg max-w-md w-full p-6">
              <div className="text-center mb-4">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${willUnpackShipment ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                  <Undo2 className={`h-6 w-6 ${willUnpackShipment ? 'text-yellow-600' : 'text-blue-600'
                    }`} />
                </div>

                <h3 className="text-lg font-medium text-gray-900">
                  {willUnpackShipment ? 'Unpack Package & Revert Shipment?' : 'Unpack Package?'}
                </h3>

                <div className="mt-2 space-y-2">
                  <p className="text-sm text-gray-500">
                    You are about to unpack <strong>Package #{unpackPackageNumber}</strong>.
                  </p>

                  {willUnpackShipment ? (
                    <>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <div className="text-left">
                            <p className="text-sm font-medium text-yellow-800">Shipment Status Will Change</p>
                            <p className="text-xs text-yellow-700 mt-1">
                              This shipment is currently fully packed. Unpacking this package will revert the shipment status from <strong>Packed</strong> to <strong>Processing</strong>.
                            </p>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500">
                        All items will remain assigned to this package unless you choose to reassign them.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">
                      All items will remain assigned to this package unless you choose to reassign them.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowUnpackConfirm(false);
                    setUnpackingPackage(null);
                    setUnpackPackageNumber(null);
                    setWillUnpackShipment(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (unpackingPackage) {
                      try {
                        const response = await fulfillmentAPI.unpackPackage(unpackingPackage);
                        if (!response.error) {
                          if (response.data?.shipment_status_updated) {
                            showToast(
                              response.data.message || 'Package unpacked. Shipment reverted to processing.',
                              'success'
                            );
                          } else {
                            showToast(response.data?.message || 'Package unpacked successfully', 'success');
                          }
                          fetchShipmentDetail();
                        } else {
                          showToast(response.error || 'Failed to unpack package', 'error');
                        }
                      } catch (error) {
                        console.error('Failed to unpack package:', error);
                        showToast('Failed to unpack package', 'error');
                      }
                    }
                    setShowUnpackConfirm(false);
                    setUnpackingPackage(null);
                    setUnpackPackageNumber(null);
                    setWillUnpackShipment(false);
                  }}
                  className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium ${willUnpackShipment
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                >
                  {willUnpackShipment ? 'Yes, Unpack & Revert' : 'Yes, Unpack Package'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShipmentDetail;